"use client";

import { useMemo, useState } from "react";
import {
  HighlightedText,
  type HighlightTone,
  type TextMatch,
} from "@/components/tool-kit/highlighted-text";
import {
  Checkbox,
  CopyButton,
  ErrorNotice,
  Notice,
  PillGroup,
  SelectField,
  StatCard,
  StatGrid,
  TextAreaField,
} from "@/components/tool-kit/primitives";
import {
  BUTTON_SECONDARY,
  HINT,
  WIDGET_H2,
  WIDGET_H3,
} from "@/components/tool-kit/styles";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";
import { t, tPlural } from "@/lib/i18n/interpolate";
import { readPoem } from "@/lib/tool-kit/poetry/pronounce";
import {
  DEFAULT_SOUND_OPTIONS,
  findSounds,
  type SoundKind,
  type SoundRun,
} from "@/lib/tool-kit/poetry/sound";
import { POETRY_READING_STRINGS } from "@/lib/tool-kit/poetry/strings";
import { usePoetryDictionary } from "@/lib/tool-kit/poetry/use-dictionary";

const PIPER = `Peter Piper picked a peck of pickled peppers;
A peck of pickled peppers Peter Piper picked;
If Peter Piper picked a peck of pickled peppers,
Where’s the peck of pickled peppers Peter Piper picked?`;

const MARINER = `The fair breeze blew, the white foam flew,
The furrow followed free;
We were the first that ever burst
Into that silent sea.`;

const SONNET_30 = `When to the sessions of sweet silent thought
I summon up remembrance of things past,
I sigh the lack of many a thing I sought,
And with old woes new wail my dear time’s waste:`;

const RE_NEWLINES = /\r\n?/g;
const KINDS: readonly SoundKind[] = ["alliteration", "assonance", "consonance"];
const TONES: readonly HighlightTone[] = [
  "amber",
  "sky",
  "rose",
  "emerald",
  "violet",
];

/** English strings. A translated content file overrides these through `ui`. */
export const ALLITERATION_FINDER_STRINGS = {
  ...POETRY_READING_STRINGS,
  textLabel: "Your poem or passage",
  textPlaceholder: PIPER,
  samplesLabel: "Try a sample:",
  samplePiper: "Peter Piper",
  sampleMariner: "Coleridge, The Ancient Mariner",
  sampleSonnet: "Shakespeare, Sonnet 30",
  samplePiperText: PIPER,
  sampleMarinerText: MARINER,
  sampleSonnetText: SONNET_30,
  sampleCredit:
    "Samples are public-domain texts: Peter Piper’s Practical Principles of Plain & Perfect Pronunciation (1813), Coleridge’s The Rime of the Ancient Mariner (1798), Shakespeare’s Sonnets (1609).",
  clear: "Clear",
  viewLegend: "Show",
  kindLabels: {
    alliteration: "Alliteration",
    assonance: "Assonance",
    consonance: "Consonance",
  },
  kindHints: {
    alliteration:
      "Words that begin with the same sound — knight and night match, cat and city do not.",
    assonance: "Three or more words sharing a stressed vowel sound.",
    consonance:
      "Three or more words ending on the same consonant sound (a closing s or z is ignored).",
  },
  gapLabel: "Words allowed between matches",
  gapOptions: ["0", "1", "2", "3", "4"],
  functionLabel: "Include function words (the, of, and…)",
  stressLabel: "Alliteration: only words stressed on the first syllable",
  emptyPrompt: "Type or paste some verse or prose to find repeated sounds.",
  heading: "Sound patterns",
  highlightLabel: "Your text with {kind} highlighted",
  statRuns: "one:{count} run|other:{count} runs",
  statWords: "one:{count} word|other:{count} words",
  statLine: "{runs}, {words}",
  runsHeading: "{kind} runs",
  runItem: "Line {line} — /{sound}/: {words}",
  runItemSpan: "Lines {line}–{last} — /{sound}/: {words}",
  noRuns: "No {kind} found with these settings.",
  skippedNotice:
    "one:{count} word is not in the pronunciation dictionary and was left out: {words}.|other:{count} words are not in the pronunciation dictionary and were left out: {words}.",
  copyRuns: "Copy list",
  copied: "Copied!",
  methodNote:
    "Sounds come from the CMU Pronouncing Dictionary, not from spelling. Words are matched within a line, and a run must stay within the gap you choose.",
} as const;

type Strings = typeof ALLITERATION_FINDER_STRINGS;

const ID = "alliteration";

function lineOffsets(text: string): number[] {
  const offsets: number[] = [];
  let at = 0;
  for (const line of text.split("\n")) {
    offsets.push(at);
    at += line.length + 1;
  }
  return offsets;
}

function matchesFor(
  runs: readonly SoundRun[],
  offsets: readonly number[],
  label: string
): TextMatch[] {
  return runs.flatMap((run) =>
    run.words.map((word, i) => ({
      start: offsets[run.lines[i]] + word.start,
      end: offsets[run.lines[i]] + word.end,
      category: run.key,
      title: `/${run.ipa}/ — ${label}`,
    }))
  );
}

function runLine(run: SoundRun, strings: Strings, fmt: (n: number) => string) {
  const first = run.lines[0] + 1;
  const last = (run.lines.at(-1) ?? run.lines[0]) + 1;
  return t(first === last ? strings.runItem : strings.runItemSpan, {
    line: fmt(first),
    last: fmt(last),
    sound: run.ipa,
    words: run.words.map((word) => word.text).join(", "),
  });
}

export default function AlliterationFinderWidget() {
  const strings = useToolUi(ALLITERATION_FINDER_STRINGS);
  const locale = useToolLocale();
  const fmt = useToolNumber();
  const [text, setText] = useState("");
  const [view, setView] = useState<SoundKind>("alliteration");
  const [gap, setGap] = useState("2");
  const [functionWords, setFunctionWords] = useState(false);
  const [firstStressed, setFirstStressed] = useState(true);
  const normalized = text.replace(RE_NEWLINES, "\n");
  const hasText = normalized.trim().length > 0;
  const { lookup, status } = usePoetryDictionary(hasText);

  const analysis = useMemo(() => {
    if (!hasText) {
      return null;
    }
    return findSounds(readPoem(normalized, lookup), {
      ...DEFAULT_SOUND_OPTIONS,
      gap: Number(gap),
      functionWords,
      firstSyllableStressed: firstStressed,
    });
  }, [hasText, normalized, lookup, gap, functionWords, firstStressed]);

  const offsets = useMemo(() => lineOffsets(normalized), [normalized]);
  const viewRuns = analysis?.runs.filter((run) => run.kind === view) ?? [];
  const keys = [...new Set(viewRuns.map((run) => run.key))];
  const tones: Record<string, HighlightTone> = {};
  for (const [i, key] of keys.entries()) {
    tones[key] = TONES[i % TONES.length];
  }
  const matches = matchesFor(viewRuns, offsets, strings.kindLabels[view]);
  const samples = [
    [strings.samplePiper, strings.samplePiperText],
    [strings.sampleMariner, strings.sampleMarinerText],
    [strings.sampleSonnet, strings.sampleSonnetText],
  ] as const;

  const stat = (kind: SoundKind) => {
    const runs = analysis?.runs.filter((run) => run.kind === kind) ?? [];
    const words = runs.reduce((sum, run) => sum + run.words.length, 0);
    return t(strings.statLine, {
      runs: tPlural(strings.statRuns, locale, runs.length, {
        count: fmt(runs.length),
      }),
      words: tPlural(strings.statWords, locale, words, { count: fmt(words) }),
    });
  };
  const listText = viewRuns.map((run) => runLine(run, strings, fmt)).join("\n");

  return (
    <>
      <TextAreaField
        id={`${ID}-text`}
        label={strings.textLabel}
        onChange={setText}
        placeholder={strings.textPlaceholder}
        prose
        rows={7}
        value={text}
      />
      <div className="flex flex-wrap items-center gap-2">
        <span className={HINT}>{strings.samplesLabel}</span>
        {samples.map(([label, body]) => (
          <button
            className={BUTTON_SECONDARY}
            key={label}
            onClick={() => setText(body)}
            type="button"
          >
            {label}
          </button>
        ))}
        <button
          className={BUTTON_SECONDARY}
          disabled={!hasText}
          onClick={() => setText("")}
          type="button"
        >
          {strings.clear}
        </button>
      </div>
      <PillGroup
        label={strings.viewLegend}
        name={`${ID}-view`}
        onChange={setView}
        options={KINDS.map((kind) => ({
          value: kind,
          label: strings.kindLabels[kind],
        }))}
        value={view}
      />
      <p className={HINT}>{strings.kindHints[view]}</p>
      <div className="grid gap-4 sm:grid-cols-2">
        <SelectField
          id={`${ID}-gap`}
          label={strings.gapLabel}
          onChange={setGap}
          options={strings.gapOptions.map((value) => ({
            value,
            label: value,
          }))}
          value={gap}
        />
        <div className="flex flex-col justify-end gap-2">
          <Checkbox
            checked={functionWords}
            label={strings.functionLabel}
            onChange={setFunctionWords}
          />
          <Checkbox
            checked={firstStressed}
            label={strings.stressLabel}
            onChange={setFirstStressed}
          />
        </div>
      </div>
      {status === "loading" ? (
        <Notice>{strings.dictionaryLoading}</Notice>
      ) : null}
      {status === "failed" ? (
        <ErrorNotice>{strings.dictionaryFailed}</ErrorNotice>
      ) : null}

      {analysis ? (
        <section aria-live="polite" className="space-y-5">
          <h2 className={WIDGET_H2}>{strings.heading}</h2>
          <StatGrid>
            {KINDS.map((kind) => (
              <StatCard
                emphasis={kind === view}
                key={kind}
                label={strings.kindLabels[kind]}
                value={stat(kind)}
              />
            ))}
          </StatGrid>
          <HighlightedText
            label={t(strings.highlightLabel, {
              kind: strings.kindLabels[view].toLocaleLowerCase(locale),
            })}
            matches={matches}
            text={normalized}
            tones={tones}
          />
          <section className="space-y-2">
            <h3 className={WIDGET_H3}>
              {t(strings.runsHeading, { kind: strings.kindLabels[view] })}
            </h3>
            {viewRuns.length > 0 ? (
              <>
                <ul className="list-disc space-y-1 pl-5 text-foreground text-sm">
                  {viewRuns.map((run) => (
                    <li
                      key={`${run.lines[0]}-${run.words[0].start}-${run.key}`}
                    >
                      {runLine(run, strings, fmt)}
                    </li>
                  ))}
                </ul>
                <CopyButton
                  copiedLabel={strings.copied}
                  label={strings.copyRuns}
                  text={listText}
                />
              </>
            ) : (
              <Notice>
                {t(strings.noRuns, {
                  kind: strings.kindLabels[view].toLocaleLowerCase(locale),
                })}
              </Notice>
            )}
          </section>
          {analysis.skipped.length > 0 ? (
            <Notice>
              {tPlural(strings.skippedNotice, locale, analysis.skipped.length, {
                count: fmt(analysis.skipped.length),
                words: analysis.skipped.join(", "),
              })}
            </Notice>
          ) : null}
          <p className={HINT}>{strings.methodNote}</p>
          <p className={HINT}>{strings.sampleCredit}</p>
        </section>
      ) : (
        <Notice>{strings.emptyPrompt}</Notice>
      )}
    </>
  );
}
